import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

export interface UserDevice {
  device_id: string;
  is_primary: boolean;
  device_name: string;
  device_code: string;
}

export function useUserDevices() {
  const { user } = useAuth();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['user-devices', user?.id],
    queryFn: async (): Promise<UserDevice[]> => {
      if (!user) return [];

      // ✅ PERFORMANCE: Single JOIN query instead of two sequential queries
      // Reduces from 2 queries to 1 per user (50% reduction)
      const { data: customerDevices, error: cdError } = await supabase
        .from('customer_devices')
        .select(`
          device_id,
          is_primary,
          devices:device_id (
            id,
            name,
            device_code
          )
        `)
        .eq('user_id', user.id)
        .order('is_primary', { ascending: false });

      if (cdError) {
        console.error('[useUserDevices] Error fetching devices:', cdError);
        throw cdError;
      }

      if (!customerDevices || customerDevices.length === 0) {
        return [];
      }

      // Map the joined result
      return customerDevices.map((cd: any) => ({
        device_id: cd.device_id,
        is_primary: cd.is_primary ?? false,
        device_name: cd.devices?.name || 'Okänd enhet',
        device_code: cd.devices?.device_code || ''
      }));
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes - devices don't change often
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return {
    devices: data || [],
    loading: isLoading,
    error,
    refetch
  };
}
